import { IBillingInfo, Country } from '@global/types/types';
import { BillingInfoFormValuesType } from '@global/components/BillingInfoForm/types';
import { CountryService } from '@global/utils/CountryService';
import { UPDATE_BILLING_INFORMATION_VARIABLES } from '@global/requests/checkout';
import { UseFormReturn } from 'react-hook-form';
import { getLocaleConfig } from '@src/global/localization/services';
import { store } from '@global/store/store';
import { RootState } from '@global/store/store';
import { PostalCodeRuleType, getPostalCodeRule } from './postalCodeRules';
import {
  validateAndorraPostalCode,
  validateBermudaPostalCode,
  validateSwedishPostalCode,
  validateBrazilianPostalCode,
  validateCanadianPostalCode,
  validateUKPostalCode,
} from './postalCodeValidations';

export function buildVariablesForUpdateBillingInfo(
  billingInfo: IBillingInfo,
  orderUid: string,
  wTOSAccepted: boolean
): UPDATE_BILLING_INFORMATION_VARIABLES {
  const result: UPDATE_BILLING_INFORMATION_VARIABLES = {
    orderUid,
    wTOSAccepted,
    CCname: billingInfo.name,
    address: {
      upaLine1: billingInfo.addressOne,
      upaLine2: billingInfo.addressTwo,
      upaCity: billingInfo.city,
      upaPostcode: billingInfo.zip,
      upaPhoneNumber: billingInfo.tel,
      wCountry: billingInfo.country.iso2Code,
      gadStateOrProvince: billingInfo.state,
      wNewAddress: billingInfo.wNewAddress,
      gadCountry: {
        name: billingInfo.country.name,
        numericCode: billingInfo.country.numericCode,
        iso2Code: billingInfo.country.iso2Code,
      },
    },
  };

  if (billingInfo?.uid) {
    result.address.uid = billingInfo.uid;
  }
  return result;
}

export function constructBillingInfo(
  oldBillingInfo: IBillingInfo,
  formValues: BillingInfoFormValuesType,
  countries: Country[],
  wNewAddress = false
): IBillingInfo {
  const countryInfo = CountryService.getCountryByIsoCode(
    countries,
    formValues.country
  );

  const billingInfo = {
    ...oldBillingInfo,
    name: `${formValues.CCname.trim()}`,
    tel: `${formValues.phone.trim()}`,
    addressOne: `${formValues.address1.trim()}`,
    addressTwo: formValues.address2?.trim(),
    city: `${formValues.city.trim()}`,
    state: getStateFromDropdown(
      countryInfo?.isoCodeTwo,
      formValues?.state?.trim(),
      countryInfo
    ),
    zip: `${formValues.zip.trim()}`,
    country: {
      name: countryInfo.name,
      numericCode: countryInfo.numCode.toString(),
      iso2Code: countryInfo.isoCodeTwo,
    },
    wNewAddress,
  };

  return billingInfo;
}

/**
 * Validates a postal code according to country-specific rules.
 * @param postalCode - The postal code to validate
 * @param countryCode - ISO 2-character country code
 * @returns Object with validation result, formatted value, and error message if invalid
 */
export const validatePostalCode = (
  postalCode: string,
  countryCode: string
): { isValid: boolean; error?: string; formatted?: string } => {
  const rule = getPostalCodeRule(countryCode);

  // No rule = no validation required
  if (!rule) {
    return { isValid: true, formatted: postalCode?.trim() };
  }

  const trimmed = postalCode?.trim() || '';

  // Handle optional postal codes
  if (!rule.required && !trimmed) {
    return { isValid: true, formatted: '' };
  }

  // Handle required postal codes
  if (rule.required && !trimmed) {
    return {
      isValid: false,
      error: `Postal code is required for ${countryCode}`,
    };
  }

  switch (rule.ruleType) {
    case PostalCodeRuleType.NO_CHECK:
      return { isValid: true, formatted: trimmed };

    case PostalCodeRuleType.EXACT_NUMERIC:
      return validateExactNumeric(trimmed, rule);

    case PostalCodeRuleType.MAX_LENGTH:
      return validateMaxLength(trimmed, rule);

    case PostalCodeRuleType.COUNTRY_SPECIFIC:
      return validateCountrySpecific(trimmed, rule, countryCode);

    default:
      return { isValid: true, formatted: trimmed };
  }
};

/**
 * Validates exact numeric postal codes (e.g., AU: 4 digits, DE: 5 digits)
 */
const validateExactNumeric = (
  postalCode: string,
  rule: any
): { isValid: boolean; error?: string; formatted?: string } => {
  const cleaned = postalCode.replace(/\s+/g, '');

  if (!/^\d+$/.test(cleaned)) {
    return { isValid: false, error: `Must be ${rule.length} digits` };
  }

  if (cleaned.length !== rule.length) {
    return { isValid: false, error: `Must be exactly ${rule.length} digits` };
  }

  return { isValid: true, formatted: cleaned };
};

/**
 * Validates maximum length postal codes (e.g., US: up to 10 characters)
 */
const validateMaxLength = (
  postalCode: string,
  rule: any
): { isValid: boolean; error?: string; formatted?: string } => {
  const cleaned = postalCode.replace(/\s+/g, '');

  if (rule.isNumeric && !/^\d+$/.test(cleaned)) {
    return { isValid: false, error: 'Must be numeric' };
  }

  if (cleaned.length > rule.length) {
    return { isValid: false, error: `Maximum ${rule.length} characters` };
  }

  return { isValid: true, formatted: cleaned };
};

/**
 * Validates country-specific formatted postal codes (e.g., CA: A1A 1A1, GB: SW1A 1AA)
 */
const validateCountrySpecific = (
  postalCode: string,
  rule: any,
  countryCode: string
): { isValid: boolean; error?: string; formatted?: string } => {
  if (countryCode === 'CA') {
    return validateCanadianPostalCode(postalCode);
  }

  if (countryCode === 'BR') {
    return validateBrazilianPostalCode(postalCode);
  }

  if (countryCode === 'SE') {
    return validateSwedishPostalCode(postalCode);
  }

  if (countryCode === 'AD') {
    return validateAndorraPostalCode(postalCode);
  }

  if (countryCode === 'BM') {
    return validateBermudaPostalCode(postalCode);
  }

  if (countryCode === 'GB') {
    return validateUKPostalCode(postalCode);
  }

  // Generic pattern validation - accept input as-is or with formatting
  if (rule.pattern && !rule.pattern.test(postalCode)) {
    return {
      isValid: false,
      error: `Invalid format. Expected: ${rule.formatDescription}`,
      formatted: postalCode,
    };
  }

  return { isValid: true, formatted: postalCode };
};

export const setZipCodeFormData = (
  zip: string,
  country: string,
  methods: UseFormReturn<BillingInfoFormValuesType>
) => {
  const validation = validatePostalCode(zip, country);
  if (validation.formatted !== undefined) {
    methods.setValue('zip', validation.formatted, {
      shouldValidate: true,
      shouldDirty: validation.formatted !== zip,
    });
  } else if (zip) {
    methods.setValue('zip', zip.trim(), {
      shouldValidate: true,
      shouldDirty: true,
    });
  }
};

/**
 * Formats postal code input in real-time as user types
 * Returns the formatted value to be set in the input
 */
export const formatPostalCodeOnChange = (
  zip: string,
  country: string
): string => {
  const validation = validatePostalCode(zip, country);
  return validation.formatted !== undefined ? validation.formatted : zip;
};

export const getStateFromDropdown = (
  countryIsoCodeTwo?: string,
  state?: string,
  country?: Country
): string => {
  const { stateDropdown } = getLocaleConfig(countryIsoCodeTwo?.toLowerCase());
  if (!stateDropdown || !state) {
    return '';
  }

  if (countryIsoCodeTwo === 'CN' && state) {
    let countryObj = country;
    if (!countryObj) {
      const { country: countryState } = store.getState() as RootState;
      countryObj = CountryService.getCountryByIsoCode(
        countryState.countries,
        countryIsoCodeTwo
      );
    }

    if (countryObj) {
      return CountryService.convertChinaStateCodeForBackend(countryObj, state);
    }
  }

  return state;
};
